// Intel screen (GDD 08, slice subset): own claims, claims on own systems, scouts and intel reports.
// Scouting and claiming a system happen on its screen (`components/system/ForeignActions.tsx`).
import { useMutation } from '@tanstack/react-query';
import { Link, useOutletContext } from 'react-router';
import { endpoints } from '../api/endpoints';
import { useClaims, useIntelReports, useScouts } from '../api/queries';
import {
  CancelClaimButton,
  claimTiming,
  OPEN_CLAIM,
  until,
  useInvalidateExpansion,
} from '../components/claims';
import type { GameContext } from '../components/GameLayout';
import { ErrorNotice, Page, Panel, Spinner } from '../components/ui';
import { formatDuration, formatNumber, formatShips } from '../labels';
import { useNow } from '../state/live';

export function IntelScreen() {
  const { galaxyId } = useOutletContext<GameContext>();
  const now = useNow();
  const refresh = useInvalidateExpansion(galaxyId);
  const mine = useClaims(galaxyId, 'mine');
  const incoming = useClaims(galaxyId, 'incoming');
  const scouts = useScouts(galaxyId);
  const reports = useIntelReports(galaxyId);
  const cancel = useMutation({
    mutationFn: (claimId: string) => endpoints.cancelClaim(galaxyId, claimId),
    onSuccess: refresh,
  });
  const systemLink = (id: string, name: string) => (
    <Link to={`/g/${galaxyId}/systems/${id}`} className="text-info hover:text-info">
      {name}
    </Link>
  );

  return (
    <Page width="wide">
      <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
        Intel &amp; claims
      </h1>

      {/* From lg: claims on the left, scouting on the right. */}
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className="space-y-4">
          <Panel title="Claims on your systems">
            {incoming.isPending ? <Spinner /> : null}
            {incoming.data?.items.length === 0 ? (
              <p className="text-sm text-muted">Nobody has claimed your systems.</p>
            ) : null}
            <ul className="space-y-2">
              {incoming.data?.items.map((c) =>
                c.view === 'owner' ? (
                  <li
                    key={c.id}
                    data-incoming-claim={c.status}
                    className="hud-cut-sm border border-danger/50 bg-danger/10 p-3 text-sm text-danger"
                  >
                    {c.claimant.displayName}
                    {c.claimant.allianceTag ? ` [${c.claimant.allianceTag}]` : ''} claims{' '}
                    {systemLink(c.systemId, c.systemName)} — {claimTiming(c, now)}
                  </li>
                ) : null
              )}
            </ul>
          </Panel>

          <Panel title="Your claims">
            {mine.isPending ? <Spinner /> : null}
            {mine.data?.items.length === 0 ? (
              <p className="text-sm text-muted">
                Claim a system from its screen. A claim activates after a day; then win a battle
                there.
              </p>
            ) : null}
            {cancel.error ? <ErrorNotice error={cancel.error} /> : null}
            <ul className="space-y-2">
              {mine.data?.items.map((c) =>
                c.view === 'claimant' ? (
                  <li
                    key={c.id}
                    data-claim-item={c.status}
                    className="flex flex-wrap items-center justify-between gap-2 hud-cut-sm border border-line bg-void/40 p-3 text-sm"
                  >
                    <span className="text-text">
                      {systemLink(c.systemId, c.systemName)} · {claimTiming(c, now)} ·{' '}
                      {formatNumber(c.creditsPaid)} Credits
                    </span>
                    {OPEN_CLAIM.has(c.status) ? (
                      <CancelClaimButton
                        claim={c}
                        label="Cancel"
                        disabled={cancel.isPending}
                        onConfirm={() => cancel.mutate(c.id)}
                      />
                    ) : null}
                  </li>
                ) : null
              )}
            </ul>
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title="Scouts">
            {scouts.isPending ? <Spinner /> : null}
            {scouts.data?.items.length === 0 ? (
              <p className="text-sm text-muted">No scouts sent yet.</p>
            ) : null}
            <ul className="space-y-2">
              {scouts.data?.items.map((s) => (
                <li
                  key={s.id}
                  data-scout-item={s.status}
                  className="hud-cut-sm border border-line bg-void/40 p-3 text-sm text-text"
                >
                  {systemLink(s.targetSystemId, s.targetSystemName)} · {formatNumber(s.influence)}{' '}
                  Influence ·{' '}
                  {s.status === 'in_transit'
                    ? `arrives ${until(s.arrivesAt, now)}`
                    : s.status === 'succeeded'
                      ? 'report ready'
                      : 'failed'}
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Intel reports">
            {reports.isPending ? <Spinner /> : null}
            {reports.data?.items.length === 0 ? (
              <p className="text-sm text-muted">Reports from your scouts appear here.</p>
            ) : null}
            <ul className="space-y-2">
              {reports.data?.items.map((r) => {
                const pirates = r.snapshot.fleets.filter((f) => f.kind === 'pirate');
                return (
                  <li
                    key={r.id}
                    data-report-id={r.id}
                    className="hud-cut-sm border border-line bg-void/40 p-3 text-sm text-text"
                  >
                    {systemLink(r.systemId, r.systemName)} ·{' '}
                    {formatDuration(now - Date.parse(r.takenAt))} ago by {r.scoutedBy.displayName}
                    {pirates.length > 0
                      ? ` · pirates: ${pirates.map((f) => formatShips(f.ships)).join(', ')}`
                      : ''}
                  </li>
                );
              })}
            </ul>
          </Panel>
        </div>
      </div>
    </Page>
  );
}
